import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button';
import { usePageContent } from '../../hooks/usePageContent';
import './index.module.css';

const DEFAULT_SORT_OPTIONS = [
  { value: 'popular',  label: 'Popular' },
  { value: 'name-asc',       label: 'A - Z' },
  { value: 'name-desc',       label: 'Z - A' },
  { value: 'price-low',  label: 'Lowest Price' },
  { value: 'price-high', label: 'Highest Price' }
];

const colors = ['red', 'yellow', 'green', 'black', 'orange', 'blue', 'white', 'purple', 'brown', 'gold', 'silver'];
const toggle = (arr, item) =>
  arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item];

const ProductFilter = ({
  categories = [],
  priceRanges = [],
  colours = [],
  sizes = [],
  vehicleTypes = [],
  filterCategory = [],
  filterPrice = [],
  filterColour = [],
  filterSize = [],
  filterVehicleType = [],
  showVehicleType = false,
  sortCriteria = 'popular',
  filteredCount = null,
  onFilterChange,
  onSortChange,
  onReset
}) => {
  const { find } = usePageContent('product-filter');
  const c = find('productFilter') || {};

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const filterRef = useRef(null);

  // Pending state — only flushed to parent on Apply
  const [pendingCategory,    setPendingCategory]    = useState(filterCategory);
  const [pendingPrice,       setPendingPrice]       = useState(filterPrice);
  const [pendingColour,      setPendingColour]      = useState(filterColour);
  const [pendingSize,        setPendingSize]        = useState(filterSize);
  const [pendingVehicleType, setPendingVehicleType] = useState(filterVehicleType);

  // Sync pending state when parent resets filters
  useEffect(() => { setPendingCategory(filterCategory); },    [JSON.stringify(filterCategory)]);
  useEffect(() => { setPendingPrice(filterPrice); },          [JSON.stringify(filterPrice)]);
  useEffect(() => { setPendingColour(filterColour); },        [JSON.stringify(filterColour)]);
  useEffect(() => { setPendingSize(filterSize); },            [JSON.stringify(filterSize)]);
  useEffect(() => { setPendingVehicleType(filterVehicleType); }, [JSON.stringify(filterVehicleType)]);

  const isDesktop = () =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 769px)').matches;

  const handleReset = () => {
    setPendingCategory([]);
    setPendingPrice([]);
    setPendingColour([]);
    setPendingSize([]);
    setPendingVehicleType([]);
    onReset();
  };

  const handleApply = () => {
    onFilterChange('category',    pendingCategory);
    onFilterChange('price',       pendingPrice);
    onFilterChange('colour',      pendingColour);
    onFilterChange('size',        pendingSize);
    if (showVehicleType) onFilterChange('vehicleType', pendingVehicleType);
    setMobileOpen(false);
    setOpenDropdown(null);
  };

  // Derive count from pending (local) state so it updates immediately on
  // checkbox toggle — even on mobile before Apply is clicked (VP-3798).
  const pendingActiveCount = pendingCategory.length + pendingPrice.length
    + pendingColour.length + pendingSize.length
    + (showVehicleType ? pendingVehicleType.length : 0);

  const applyLabel = (c.applyFiltersTextCount || `Apply filters (${pendingActiveCount})`).replace('{count}', pendingActiveCount);

  const toggleDropdown = (key) => {
    setOpenDropdown(prev => prev === key ? null : key);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sortOptions = c.sortOptions || DEFAULT_SORT_OPTIONS;

  const formatSizeLabel = (code) => {
    if (code === 'ST') return c.sizeST || 'Standard';
    if (code === 'SL') return c.sizeSL || 'Slimline';
    if (code === 'OOE') return c.sizeOOE || '1 Standard & 1 Slimline';
    return code;
  };

  const formatPriceLabel = (range) =>
    `${range.replace('+', ' and above')}`;

  const controlName = (key, label, isActive) => (
    <>
      <h3
        id={`filter-name-${key}`}
        className="product-filter__control-name product-filter__control-name--mobile"
      >
        {label}
      </h3>
      <button
        type="button"
        aria-expanded={String(openDropdown === key)}
        className={[
          'product-filter__control-name',
          'product-filter__control-name--desktop',
          openDropdown === key ? 'product-filter__control-name--open' : '',
          isActive ? 'product-filter__control-name--active' : ''
        ].filter(Boolean).join(' ')}
        onClick={() => toggleDropdown(key)}
      >
        {label}
      </button>
    </>
  );

  const dropdownClass = (key) =>
    `product-filter__dropdown${openDropdown === key ? ' product-filter__dropdown--open' : ''}`;

  return (
    <div role="group" aria-labelledby="filter-title" className={`product-filter product-filter--ready${mobileOpen ? ' product-filter--fixed product-filter--above-header' : ''}`} ref={filterRef}>
      <div className="product-filter__inner">
        <div className="product-filter__header">
          <h2 className="product-filter__title product-filter__title--text" id="filter-title">
            {c.filterTitleText || 'Filter Plate Styles'}
          </h2>
          <button
            type="button"
            aria-expanded={String(mobileOpen)}
            className={`product-filter__title product-filter__title--button${mobileOpen ? ' product-filter__title--open' : ''}`}
            onClick={() => setMobileOpen(v => !v)}
          >
            <span>{c.filterButtonText || 'Filter Plate Styles'}</span>
          </button>
        </div>

        <div className={`product-filter__controls${mobileOpen ? ' product-filter__controls--visible' : ''}`}>

          {showVehicleType && vehicleTypes.length > 0 && (
            <div role="group" aria-labelledby="filter-name-vehicleType" className="product-filter__control">
              {controlName('vehicleType', 'Vehicle Type', filterVehicleType.length > 0)}
              <div className={dropdownClass('vehicleType')}>
                <div className="product-filter__checklist">
                  <div className="product-filter__checklist-inner">
                    {vehicleTypes.map(type => (
                      <div key={type} className="product-filter__checklist-control">
                        <input
                          className="product-filter__checklist-control-input"
                          type="checkbox"
                          id={`filter-vehicleType-${type}`}
                          name="filter-vehicleType"
                          value={type}
                          checked={pendingVehicleType.includes(type)}
                          onChange={() => { const next = toggle(pendingVehicleType, type); setPendingVehicleType(next); if (isDesktop()) onFilterChange('vehicleType', next); }}
                        />
                        <label
                          className="product-filter__checklist-control-label"
                          htmlFor={`filter-vehicleType-${type}`}
                        >
                          {type}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {categories.length > 0 && (
            <div role="group" aria-labelledby="filter-name-category" className="product-filter__control">
              {controlName('category', 'Category', filterCategory.length > 0)}
              <div className={dropdownClass('category')}>
                <div className="product-filter__checklist">
                  <div className="product-filter__checklist-inner">
                    {categories.map(category => (
                      <div key={category} className="product-filter__checklist-control">
                        <input
                          className="product-filter__checklist-control-input"
                          type="checkbox"
                          id={`filter-category-${category}`}
                          name="filter-category"
                          value={category}
                          checked={pendingCategory.includes(category)}
                          onChange={() => { const next = toggle(pendingCategory, category); setPendingCategory(next); if (isDesktop()) onFilterChange('category', next); }}
                        />
                        <label
                          className="product-filter__checklist-control-label"
                          htmlFor={`filter-category-${category}`}
                        >
                          {category}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {priceRanges.length > 0 && (
            <div role="group" aria-labelledby="filter-name-price" className="product-filter__control">
              {controlName('price', 'Price', filterPrice.length > 0)}
              <div className={dropdownClass('price')}>
                <div className="product-filter__checklist">
                  <div className="product-filter__checklist-inner">
                    {priceRanges.map(range => (
                      <div key={range} className="product-filter__checklist-control">
                        <input
                          className="product-filter__checklist-control-input"
                          type="checkbox"
                          id={`filter-price-${range}`}
                          name="filter-price"
                          value={range}
                          checked={pendingPrice.includes(range)}
                          onChange={() => { const next = toggle(pendingPrice, range); setPendingPrice(next); if (isDesktop()) onFilterChange('price', next); }}
                        />
                        <label
                          className="product-filter__checklist-control-label"
                          htmlFor={`filter-price-${range}`}
                        >
                          {formatPriceLabel(range)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {colours.length > 0 && (
            <div role="group" aria-labelledby="filter-name-colour" className="product-filter__control">
              {controlName('colour', 'Colour', filterColour.length > 0)}
              <div className={dropdownClass('colour')}>
                <div className="product-filter__colourlist">
                  <div className="product-filter__colourlist-inner">
                    {colors.map(color => (
                      <div key={color} className="product-filter__colourlist-colour">
                        <input
                          className="product-filter__colourlist-colour-input"
                          type="checkbox"
                          id={`filter-colour-${color}`}
                          name="filter-colour"
                          value={color}
                          checked={pendingColour.includes(color)}
                          onChange={() => { const next = toggle(pendingColour, color); setPendingColour(next); if (isDesktop()) onFilterChange('colour', next); }}
                        />
                        <label
                          className={`product-filter__colourlist-colour-label  product-filter__colourlist-colour-label--${color}`}
                          htmlFor={`filter-colour-${color}`}>
                          <span className="visuallyhidden">{color}</span>
                          <span className="product-filter__colourlist-colour-label-text" aria-hidden="true">A</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {sizes.length > 0 && (
            <div role="group" aria-labelledby="filter-name-size" className="product-filter__control">
              {controlName('size', 'Size', filterSize.length > 0)}
              <div className={dropdownClass('size')}>
                <div className="product-filter__checklist">
                  <div className="product-filter__checklist-inner">
                    {sizes.filter(s => s.code !== 'OOE').map(size => (
                      <div key={size.code} className="product-filter__checklist-control">
                        <input
                          className="product-filter__checklist-control-input"
                          type="checkbox"
                          id={`filter-size-${size.code}`}
                          name="filter-size"
                          value={size.code}
                          checked={pendingSize.includes(size.code)}
                          onChange={() => { const next = toggle(pendingSize, size.code); setPendingSize(next); if (isDesktop()) onFilterChange('size', next); }}
                        />
                        <label
                          className={`product-filter__checklist-control-label product-filter__checklist-control-label--size product-filter__checklist-control-label--${size.code.toLowerCase()}`}
                          htmlFor={`filter-size-${size.code}`}
                        >
                          {formatSizeLabel(size.code)}
                          <span className="product-filter__checklist-control-label-corner product-filter__checklist-control-label-corner--top-left" />
                          <span className="product-filter__checklist-control-label-corner product-filter__checklist-control-label-corner--top-right" />
                          <span className="product-filter__checklist-control-label-corner product-filter__checklist-control-label-corner--bottom-left" />
                          <span className="product-filter__checklist-control-label-corner product-filter__checklist-control-label-corner--bottom-right" />
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div role="group" aria-labelledby="filter-name-sort" className="product-filter__control">
            {controlName('sort', 'Sort', false)}
            <div className={dropdownClass('sort')}>
              <div className="product-filter__checklist">
                <div className="product-filter__checklist-inner">
                  {sortOptions.map(option => (
                    <div key={option.value} className="product-filter__checklist-control">
                      <input
                        className="product-filter__checklist-control-input"
                        type="radio"
                        id={`filter-sort-${option.value}`}
                        name="filter-sort"
                        value={option.value}
                        checked={sortCriteria === option.value}
                        onChange={(e) => onSortChange(e.target.value)}
                      />
                      <label
                        className="product-filter__checklist-control-label product-filter__checklist-control-label--circular"
                        htmlFor={`filter-sort-${option.value}`}
                      >
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="product-filter__actions product-filter__actions--mobile">
            <button className="product-filter__reset" type="button" onClick={handleReset}>
              <span className="visuallyhidden">{c.resetFilterText || 'Reset filter'}</span>
            </button>
            <button type="button" className="button" onClick={handleApply}>
              <span className="button__text">{applyLabel}</span>
            </button>
          </div>

          <div className="product-filter__actions product-filter__actions--desktop">
            <Button variant="tertiary" onClick={handleReset}>
              {c.resetFilterText || 'Reset filter'}
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
};

ProductFilter.propTypes = {
  categories: PropTypes.array,
  priceRanges: PropTypes.array,
  colours: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, hex: PropTypes.string })),
  sizes: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string })),
  vehicleTypes: PropTypes.array,
  filterCategory: PropTypes.array,
  filterPrice: PropTypes.array,
  filterColour: PropTypes.array,
  filterSize: PropTypes.array,
  filterVehicleType: PropTypes.array,
  showVehicleType: PropTypes.bool,
  sortCriteria: PropTypes.string,
  onFilterChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired
};

export default ProductFilter;
